import { FirebaseError, initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  OAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  setPersistence,
  type Auth,
  type User,
  type UserCredential,
} from "firebase/auth";
import {
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";
import type { FirebasePublicConfig } from "../types";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let appCheck: AppCheck | null = null;
let initialization: Promise<Auth> | null = null;

async function loadPublicConfig(): Promise<FirebasePublicConfig> {
  const environmentConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: "project-3c057e65-b6ad-4842-9d8",
    storageBucket: "project-3c057e65-b6ad-4842-9d8.firebasestorage.app",
    messagingSenderId: "806754367551",
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY,
  };

  if (environmentConfig.apiKey && environmentConfig.authDomain &&
      environmentConfig.appId && environmentConfig.appCheckSiteKey) {
    return environmentConfig as FirebasePublicConfig;
  }

  const response = await fetch("/api/web-config", { cache: "no-store" });
  if (!response.ok) {
    throw new Error("Pearlie web access is still being prepared. Try again shortly.");
  }
  return response.json() as Promise<FirebasePublicConfig>;
}

export function initializeFirebase(): Promise<Auth> {
  if (initialization) return initialization;
  initialization = (async () => {
    const config = await loadPublicConfig();
    app = initializeApp(config);

    if (location.hostname === "127.0.0.1" || location.hostname === "localhost") {
      (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(config.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    auth = getAuth(app);
    await setPersistence(auth, browserLocalPersistence);

    await new Promise<void>((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth!, async user => {
        unsubscribe();
        if (user) {
          resolve();
          return;
        }
        try {
          await signInAnonymously(auth!);
          resolve();
        } catch (error) {
          reject(error);
        }
      }, reject);
    });
    return auth;
  })();
  return initialization;
}

export async function currentUser(): Promise<User> {
  const readyAuth = await initializeFirebase();
  if (readyAuth.currentUser) return readyAuth.currentUser;
  return (await signInAnonymously(readyAuth)).user;
}

export async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  const user = await currentUser();
  const idToken = await user.getIdToken(forceRefresh);
  if (!appCheck) throw new Error("Pearlie could not verify this browser.");
  const checkToken = await getToken(appCheck, forceRefresh);
  return {
    Authorization: `Bearer ${idToken}`,
    "X-Firebase-AppCheck": checkToken.token,
  };
}

async function linkOrSignInWithPopup(provider: GoogleAuthProvider | OAuthProvider): Promise<UserCredential> {
  const readyAuth = await initializeFirebase();
  const user = readyAuth.currentUser;
  if (!user || !user.isAnonymous) return signInWithPopup(readyAuth, provider);

  try {
    return await linkWithPopup(user, provider);
  } catch (error) {
    const credential = provider instanceof GoogleAuthProvider
      ? GoogleAuthProvider.credentialFromError(error as FirebaseError)
      : OAuthProvider.credentialFromError(error as FirebaseError);
    if (credential) return signInWithCredential(readyAuth, credential);
    throw error;
  }
}

export async function signInWithGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return linkOrSignInWithPopup(provider);
}

export async function signInWithApple(): Promise<UserCredential> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return linkOrSignInWithPopup(provider);
}

export async function signInOrCreateWithEmail(email: string, password: string): Promise<UserCredential> {
  const readyAuth = await initializeFirebase();
  const user = readyAuth.currentUser;
  if (user?.isAnonymous) {
    try {
      const credential = EmailAuthProvider.credential(email, password);
      return await linkWithCredential(user, credential);
    } catch {
      try {
        return await createUserWithEmailAndPassword(readyAuth, email, password);
      } catch {
        return signInWithEmailAndPassword(readyAuth, email, password);
      }
    }
  }
  try {
    return await signInWithEmailAndPassword(readyAuth, email, password);
  } catch {
    return createUserWithEmailAndPassword(readyAuth, email, password);
  }
}

export async function leaveAccountForPrivateSession(): Promise<User> {
  const readyAuth = await initializeFirebase();
  await signOut(readyAuth);
  return (await signInAnonymously(readyAuth)).user;
}

export async function deleteFirebaseAccount(): Promise<void> {
  const user = await currentUser();
  await deleteUser(user);
}

export async function watchUser(listener: (user: User) => void): Promise<() => void> {
  const readyAuth = await initializeFirebase();
  return onAuthStateChanged(readyAuth, user => {
    if (user) listener(user);
  });
}
